import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { AxiosWrapper } from 'services/AxiosWrapper';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import { injectJwtTokenIntoHeaders } from '../tokenLocalStorage';
import { stringify } from 'qs';
import { IMarketingManagerOpportunitiesCardsResponse, IMarketingManagerOpportunitiesResponse } from './types';
import { ESortOrder } from 'store/common/types';

// Export all types
export * from './types';

export class MarketingManagerApiService<T extends AxiosWrapper<AxiosInstance>> {
  client: T;

  constructor(client: T) {
    this.client = client;
  }

  // this is Step 1 of the upload process
  getContentUploadUrl = async ({
    contentUuid,
    contentType,
    fileName,
  }: {
    contentUuid: string;
    contentType: string;
    fileName: string;
  }): Promise<AxiosResponse> => {
    const endpoint = `/opportunity/v1/media/upload-url/${contentUuid}`;
    return this.client.post(endpoint, {
      contentType,
      fileName,
    });
  };

  // this is Step 3 of the upload process
  postContentProcessUpload = async ({
    contentUuid,
    displayName,
    filePath,
    fileType,
  }: {
    contentUuid: string;
    displayName: string;
    filePath: string;
    fileType: 'image' | 'file';
  }): Promise<AxiosResponse> => {
    const endpoint = `/opportunity/v1/media/process-upload/${contentUuid}`;
    return this.client.post(endpoint, {
      displayName,
      filePath,
      fileType,
    });
  };

  // Create new opportunity
  createOpportunity = async (opportunityData: import('./types').IMarketingManagerOpportunityRequest): Promise<AxiosResponse> => {
    const endpoint = '/opportunity/v1/opportunities';
    return this.client.post(endpoint, opportunityData);
  };

  // Fetch categories
  getCategories = async (): Promise<AxiosResponse<import('./types').IMarketingManagerCategoriesResponse>> => {
    const endpoint = '/opportunity/v1/categories';
    return this.client.get(endpoint);
  };

  // Fetch product types  
  getProductTypes = async (): Promise<AxiosResponse<import('./types').IMarketingManagerProductTypesResponse>> => {
    const endpoint = '/opportunity/v1/product-types';
    return this.client.get(endpoint);
  };

  // Fetch single opportunity by UUID
  getOpportunity = async (uuid: string): Promise<AxiosResponse<import('./types').IMarketingManagerOpportunityResponse>> => {
    const endpoint = `/opportunity/v1/opportunities/${uuid}`;
    return this.client.get(endpoint);
  };

  // Update existing opportunity
  updateOpportunity = async (uuid: string, opportunityData: import('./types').IMarketingManagerOpportunityUpdateRequest): Promise<AxiosResponse<import('./types').IMarketingManagerOpportunityResponse>> => {
    const endpoint = `/opportunity/v1/opportunities/${uuid}`;
    return this.client.put(endpoint, opportunityData);
  };

  // Get paginated list of opportunities
  getOpportunities = async (params: {
    page: number;
    limit: number;
    sortBy: string;
    order: ESortOrder;
    // Filter params
    categoryUuid?: string;
    status?: 'Draft' | 'Active' | 'Scheduled' | 'Expired';
    crmCompany?: string;
    destination?: string;
    activeFrom?: string;
    activeTo?: string;
    title?: string;
  }): Promise<AxiosResponse<import('./types').IMarketingManagerOpportunityListResponse>> => {
    const endpoint = '/opportunity/v1/opportunities';
    return this.client.get(endpoint, { params });
  };

  // List opportunities Cards (marketing content)
  listOpportunitiesCards = async ({
    limit = 12,
    page = 1,
    sortBy = 'creationDate',
    order = ESortOrder.DESC,
    categoryUuids,
    destinations,
  }: {
    limit?: number;
    page?: number;
    sortBy?: string;
    order?: ESortOrder;
    categoryUuids?: string[];
    destinations?: string[];
  }): Promise<AxiosResponse<IMarketingManagerOpportunitiesCardsResponse>> => {
    const endpoint = `/opportunity/v1/opportunities/cards`;
    const params: any = {
      limit,
      page,
      sortBy,
      order,
    };

    // Only add filter params if they have values
    if (categoryUuids && categoryUuids.length > 0) {
      params.categoryUuids = categoryUuids;
    }
    if (destinations && destinations.length > 0) {
      params.destinations = destinations;
    }

    return this.client.get(endpoint, { params });
  };

  // Delete opportunity by UUID
  deleteOpportunity = async (uuid: string): Promise<AxiosResponse> => {
    const endpoint = `/opportunity/v1/opportunities/${uuid}`;
    return this.client.delete(endpoint);
  };
}

export const makeMarketingManagerApi = (): MarketingManagerApiService<AxiosWrapper<AxiosInstance>> => {
  const dynamicParameters = ParameterService.getParameters();
  const headers: any = {};

  const client = axios.create({
    baseURL: dynamicParameters.MARKETING_MANAGER_BASE_URL,
    headers: injectJwtTokenIntoHeaders(headers),
    withCredentials: true,
    paramsSerializer: stringify,
  });

  const clientWrapper = new AxiosWrapper(client);

  return new MarketingManagerApiService<AxiosWrapper<AxiosInstance>>(clientWrapper);
};
